import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AddFeedbackOptions,
  AddFeedbackResponse,
  AskQuestionResponse,
  BulkUpdateMessageTagsByFilterOptions,
  BulkUpdateMessageTagsByFilterResponse,
  BulkUpdateMessageTagsOptions,
  BulkUpdateMessageTagsResponse,
  Message,
  MessageQuery,
  MessagesQueryBuilder,
  QueryMessagesResponse,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  addFeedback as universalAddFeedback,
  askQuestion as universalAskQuestion,
  bulkUpdateMessageTags as universalBulkUpdateMessageTags,
  bulkUpdateMessageTagsByFilter as universalBulkUpdateMessageTagsByFilter,
  queryMessages as universalQueryMessages,
  typedQueryMessages as universalTypedQueryMessages,
  updateExtendedFields as universalUpdateExtendedFields,
} from './innovation-playground-v2-message-assistant-training.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ai-site-chat' };

export function queryMessages(httpClient: HttpClient): QueryMessagesSignature {
  return () =>
    universalQueryMessages(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryMessagesSignature {
  /**
   * Creates a query to retrieve a list of messages.
   * The `queryMessages()` function builds a query to retrieve a list of messages and returns a `MessagesQueryBuilder` object.
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   * You can refine the query by chaining `MessagesQueryBuilder` functions onto the query. `MessagesQueryBuilder` functions enable you to sort, filter, and control the results `queryMessages()` returns.
   * `queryMessages()` runs with these `MessagesQueryBuilder` defaults, which you can override:
   *
   * *`skip(0)`
   * * `limit(50)`
   * * `ascending("createdDate")`
   *
   * The functions that are chained to `queryMessages()` are applied in the order they are called. For example, if you apply `ascending('createdDate')` and then `descending('id')`, the results are sorted first by the created date, and then, if there are multiple results with the same created date, the messages are sorted by ID.
   *
   * ## Supported Filters and Sorting
   *
   * | Field | Supported Filters | Sortable  |
   * |----------------|------------------------------------------------------------------------------------------|-----------|
   * | `id`           | [All operators](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#operators). | Sortable  |
   * | `createdDate`  | [All operators](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#operators). | Sortable  |
   * @deprecated
   */
  (): MessagesQueryBuilder;
}

export function typedQueryMessages(
  httpClient: HttpClient
): TypedQueryMessagesSignature {
  return (query: MessageQuery) =>
    universalTypedQueryMessages(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryMessagesSignature {
  /** */
  (query: MessageQuery): Promise<
    NonNullablePaths<
      QueryMessagesResponse,
      | `messages`
      | `messages.${number}._id`
      | `messages.${number}.conversationId`
      | `messages.${number}.sender`
      | `messages.${number}.feedback.document._id`
      | `messages.${number}.feedback.document.title`
      | `messages.${number}.feedback.document.description`
      | `messages.${number}.feedback.document.url`
      | `messages.${number}.feedback.document.category`
      | `messages.${number}.feedback.document.action`
      | `messages.${number}.feedback.type`,
      6
    >
  >;
}

export function askQuestion(httpClient: HttpClient): AskQuestionSignature {
  return (question: Message) =>
    universalAskQuestion(
      question,
      // @ts-ignore
      { httpClient }
    );
}

interface AskQuestionSignature {
  /**
   * Asks a question to the AI Assistant.
   *
   * This method creates and sends a message from a Wix user.
   * @param - Question message.
   * @deprecated
   */
  (question: Message): Promise<
    NonNullablePaths<
      AskQuestionResponse,
      | `answer._id`
      | `answer.conversationId`
      | `answer.sender`
      | `answer.feedback.document._id`
      | `answer.feedback.document.title`
      | `answer.feedback.document.description`
      | `answer.feedback.document.tags`
      | `answer.feedback.document.url`
      | `answer.feedback.document.category`
      | `answer.feedback.document.action`
      | `answer.feedback.document.reference._id`
      | `answer.feedback.document.reference.documentType`
      | `answer.feedback.document.reference.title`
      | `answer.feedback.document.reference.url`
      | `answer.feedback.document.reference.img`
      | `answer.feedback.type`
      | `answer.body.additionalData`
      | `answer.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function addFeedback(httpClient: HttpClient): AddFeedbackSignature {
  return (
    messageId: string,
    options: NonNullablePaths<AddFeedbackOptions, `feedback` | `revision`, 2>
  ) =>
    universalAddFeedback(
      messageId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AddFeedbackSignature {
  /**
   * Adds feedback to a message.
   * @param - ID of the message to add feedback to.
   * @deprecated
   */
  (
    messageId: string,
    options: NonNullablePaths<AddFeedbackOptions, `feedback` | `revision`, 2>
  ): Promise<
    NonNullablePaths<
      AddFeedbackResponse,
      | `message._id`
      | `message.conversationId`
      | `message.sender`
      | `message.feedback.document._id`
      | `message.feedback.document.title`
      | `message.feedback.document.description`
      | `message.feedback.document.tags`
      | `message.feedback.document.url`
      | `message.feedback.document.category`
      | `message.feedback.document.action`
      | `message.feedback.document.reference._id`
      | `message.feedback.document.reference.documentType`
      | `message.feedback.document.reference.title`
      | `message.feedback.document.reference.url`
      | `message.feedback.document.reference.img`
      | `message.feedback.type`
      | `message.body.additionalData`
      | `message.tags.privateTags.tagIds`,
      6
    >
  >;
}

export function bulkUpdateMessageTags(
  httpClient: HttpClient
): BulkUpdateMessageTagsSignature {
  return (
    ids: string[],
    options?: NonNullablePaths<BulkUpdateMessageTagsOptions, `assignTags`, 2>
  ) =>
    universalBulkUpdateMessageTags(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMessageTagsSignature {
  /**
   * Synchronously updates tags on a list of messages, specified using the messages' IDs.
   *
   * If a tag appears in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Message IDs. Tags are updated for messages included in this list.
   * @deprecated
   */
  (
    ids: string[],
    options?: NonNullablePaths<BulkUpdateMessageTagsOptions, `assignTags`, 2>
  ): Promise<
    NonNullablePaths<
      BulkUpdateMessageTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateMessageTagsByFilter(
  httpClient: HttpClient
): BulkUpdateMessageTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateMessageTagsByFilterOptions,
      `assignTags`,
      2
    >
  ) =>
    universalBulkUpdateMessageTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateMessageTagsByFilterSignature {
  /**
   * Asynchronously updates tags on a list of messages, specified by applying a filter to all messages. If a filter is not specified, this method updates all messages.
   *
   * If a tag appears in both `assignTags` and `unassignTags`, it is assigned.
   * @param - Filter to apply to the messages. Tags are updated for messages included in this filter.
   * @deprecated
   */
  (
    filter: Record<string, any>,
    options?: NonNullablePaths<
      BulkUpdateMessageTagsByFilterOptions,
      `assignTags`,
      2
    >
  ): Promise<
    NonNullablePaths<BulkUpdateMessageTagsByFilterResponse, `jobId`, 2>
  >;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields. This method does not increment the revision.
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   * @deprecated
   */
  (
    _id: string,
    namespace: string,
    options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
  ): Promise<
    NonNullablePaths<
      UpdateExtendedFieldsResponse,
      | `message._id`
      | `message.conversationId`
      | `message.sender`
      | `message.feedback.document._id`
      | `message.feedback.document.title`
      | `message.feedback.document.description`
      | `message.feedback.document.tags`
      | `message.feedback.document.url`
      | `message.feedback.document.category`
      | `message.feedback.document.action`
      | `message.feedback.document.reference._id`
      | `message.feedback.document.reference.documentType`
      | `message.feedback.document.reference.title`
      | `message.feedback.document.reference.url`
      | `message.feedback.document.reference.img`
      | `message.feedback.type`
      | `message.body.additionalData`
      | `message.tags.privateTags.tagIds`,
      6
    >
  >;
}

export {
  Action,
  ActionEvent,
  AddFeedbackOptions,
  AddFeedbackRequest,
  AddFeedbackResponse,
  ApplicationError,
  AskQuestionRequest,
  AskQuestionResponse,
  Body,
  BulkActionMetadata,
  BulkUpdateMessageTagsByFilterOptions,
  BulkUpdateMessageTagsByFilterRequest,
  BulkUpdateMessageTagsByFilterResponse,
  BulkUpdateMessageTagsOptions,
  BulkUpdateMessageTagsRequest,
  BulkUpdateMessageTagsResponse,
  BulkUpdateMessageTagsResult,
  Category,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  Document,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  ExtendedFields,
  Feedback,
  FeedbackType,
  IdentificationData,
  IdentificationDataIdOneOf,
  ItemMetadata,
  Message,
  MessageEnvelope,
  MessageQuerySpec,
  MessagesQueryBuilder,
  MessagesQueryResult,
  QueryMessagesRequest,
  QueryMessagesResponse,
  ReferenceDocument,
  RestoreInfo,
  Sender,
  SortOrder,
  Sorting,
  TagList,
  Tags,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  WebhookIdentityType,
  utils,
} from './innovation-playground-v2-message-assistant-training.universal.js';
